import { memo, useCallback, useEffect, useState } from 'react';

import { Pagination, Spin } from 'antd';

import type IPagination from 'interfaces/IPagination';
import type { IFinancialEntry, SomeFinancialStatementTabs } from 'v2/interfaces/financial';
import { InitialPagination } from 'v2/services/financialStatement/statement';

import ContentItem from './ContentItem';

interface IProps {
  id: string;
  data: IFinancialEntry[];
  pagination: IPagination;
  loading: boolean;
  handlePagination: {
    onChangeRowsPerPage: (per_page: number) => void;
    onChangePage: (page: number) => void;
  };
  balanceLabel: string;
  showBlockedDetails?: boolean;
  currentTab?: SomeFinancialStatementTabs;
}

function ContentTable({
  id,
  data,
  pagination,
  loading,
  handlePagination,
  balanceLabel,
  showBlockedDetails,
  currentTab
}: IProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const handleChangeCollapse = useCallback(
    (newIndex: number) => setSelectedIndex(prevIndex => (newIndex !== prevIndex ? newIndex : null)),
    []
  );

  useEffect(() => {
    if (!loading && data) {
      return setSelectedIndex(0);
    }
  }, [data, loading]);

  return (
    <div>
      {loading && (
        <div className='flex justify-center py-4'>
          <Spin />
        </div>
      )}
      <div id={id}>
        {data?.map((entry: IFinancialEntry, index: number) => (
          <ContentItem
            selected={index === selectedIndex}
            handleSelect={handleChangeCollapse}
            data={entry}
            index={index}
            key={`ContentItem-${entry.transaction_date}`}
            balanceLabel={balanceLabel}
            showBlockedDetails={showBlockedDetails}
            currentTab={currentTab}
          />
        ))}
      </div>

      {!loading && (pagination?.total_pages > 1 || pagination?.per_page > InitialPagination.per_page) && (
        <div className='mt-4 flex justify-end'>
          <Pagination
            total={pagination?.total_items || 0}
            current={pagination?.current_page || 1}
            pageSize={pagination?.per_page || 15}
            pageSizeOptions={[15, 30, 50, 100, 200]}
            showSizeChanger
            onChange={page => handlePagination.onChangePage(page)}
            onShowSizeChange={(_, size) => handlePagination.onChangeRowsPerPage(size)}
          />
        </div>
      )}
    </div>
  );
}

export default memo(ContentTable);
